/**
 * 이상한 방 - 원래 방 방문 타이머 (체류 · 쿨타임)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.4절(원래 방 - 워프 방식)
 *            `이상한방-상태모델-v0.1.md` 11.3절(서버가 쥐는 것)
 *
 * ## 왜 `gameState.ts`에서 뽑았는가
 *
 * 원래 방의 세 숫자(들어가 있는가 · 얼마나 머물렀는가 · 재입장까지 얼마나 남았는가)는
 * 싱글에서는 라운드에 하나지만, 멀티에서는 **술래마다 하나**다(4.4절 "정원 1"은 문 토큰의
 * 문제이고, 체류 상한과 쿨타임은 사람마다 따로 흐른다). 매치 리듀서가 `GameState`를
 * N벌 들 수는 없으므로, 산술만 이 파일로 내리고 `GameState`와 매치의 좌석이 **같은 함수**를
 * 부르게 한다. 효과 방출(`warpTo`·`syncProxies`·소리)은 각 리듀서가 자기 방식으로 한다 -
 * 이 파일은 상태 산술만 답한다.
 *
 * `OriginVisit`은 구조 타입이다. 필드 이름을 `GameState`의 것과 똑같이 둔 이유는
 * `GameState`를 **그대로** 넘길 수 있게 하기 위해서다 - 어댑터가 하나라도 생기면
 * 그 어댑터가 두 번째 규칙 사본이 된다.
 *
 * ## 규칙 둘
 *
 *  1. **수동 퇴장과 자동 퇴장의 결과 상태가 같다.** 둘이 갈라지면 자동 퇴장에만 쿨타임을
 *     거는 식의 구멍이 생기고, 그 구멍이 곧 깜빡비교(수색 중 원본↔변경본 교차 = 정답 자동 추출)다.
 *     `tickOriginVisit`이 체류 상한을 넘기면 `exitOriginVisit`을 **그대로** 부른다.
 *  2. **쿨타임은 0 아래로 내려가지 않고 체류는 상한에서 끊긴다.** 모든 산술이 "0으로 클램프 /
 *     ≥ 비교" 꼴이라 한 구간 안에서는 `tick(a); tick(b)`와 `tick(a + b)`가 같다 - 호스트가
 *     사람 이벤트 직전에 tick을 선적용해도 결과가 달라지지 않는 근거다(병합 불변).
 *     구간을 **넘는** 병합(체류 → 쿨타임)은 같지 않다: 자동 퇴장의 초과분을 쿨타임에서 깎지
 *     않는 것이 기존 동작이고, 이 파일은 동작을 바꾸지 않는다.
 *
 * ## 이 파일이 하지 않는 것
 *
 * `dtMs` 검증(유한수 · 0 이상)은 리듀서의 `tick` 입구가 한다. 여기서 또 던지면
 * 같은 사고에 두 개의 메시지가 생긴다.
 */

import { MS_PER_SECOND } from '../core/time'
import type { Tuning } from './tuning'

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 한 사람의 원래 방 방문 상태. `GameState`가 구조적으로 이 타입을 만족한다.
 *
 * 세 필드가 한 덩어리인 이유: 입장·퇴장 전이는 셋을 **함께** 바꾼다(입장은 체류를 0으로, 퇴장은
 * 체류를 0으로 그리고 쿨타임을 가득으로). 셋을 따로 들면 그 전이가 반드시 어긋난다.
 * 페이즈 종료(리빌·결과 진입)는 예외다 - "안에 있다"는 사실만 지우고 쿨타임은 남긴다. 리빌 중
 * 쿨타임이 흐를 일은 없으니 지워도 결과는 같지만, 그것은 `gameState`가 결정하는 일이고 이 파일은
 * 그 전이를 갖지 않는다.
 */
export type OriginVisit = Readonly<{
  isInOriginRoom: boolean
  /** 원래 방 체류 경과. 상한을 넘으면 자동 퇴장한다. */
  originStayElapsedMs: number
  /** 재입장까지 남은 쿨타임. 0이어야 입장할 수 있다. */
  originCooldownMs: number
}>

/** 이 파일이 읽는 튜닝 값 둘. `Tuning` 전체가 구조적으로 만족하므로 그대로 넘긴다. */
export type OriginVisitTuning = Pick<Tuning, 'originStaySeconds' | 'originCooldownSeconds'>

/** `tickOriginVisit`의 결과. `exited`가 참이면 이 tick이 체류 상한을 넘겨 자동 퇴장시켰다. */
export type OriginVisitTick = Readonly<{ visit: OriginVisit; exited: boolean }>

/**
 * 원래 방 버튼이 가질 수 있는 네 가지 얼굴. HUD(`ui/gameHud.ts`)가 그리는 모양이지만 타입이 **여기** 사는 이유는
 * 싱글 뷰(`sessionView.originStatusOf`)와 매치 뷰(`match/matchView.ts`)가 같은 방문 상태에서 같은 얼굴을 만들기
 * 때문이다 - 규칙 층은 `ui`를 import 할 수 없으므로 두 뷰가 공유할 자리는 그 둘 아래인 이 리프뿐이다.
 *
 * 상태를 문자열 하나로 합치지 않고 남은 시간을 함께 들고 다니는 이유: 쿨타임과 체류는 **숫자가 줄어드는 것 자체가
 * 연출**이다(컨셉시트 4.4의 방어선이 바로 이 시간이다). `disabled` 불리언만 넘기면 "왜 안 눌리는지"를 화면이 설명할 수 없다.
 */
export type OriginRoomStatus =
  /** 지금 워프할 수 있다. */
  | Readonly<{ kind: 'ready' }>
  /** 원래 방 체류 중. `remainingMs`가 0이 되면 상태머신이 자동 퇴장시킨다. */
  | Readonly<{ kind: 'staying'; remainingMs: number; totalMs: number }>
  /**
   * 재입장 쿨타임. 이것이 깜빡비교를 막는 유일한 장치다(`tuning.originCooldownSeconds`).
   * 길이가 5초냐 30초냐에 따라 **표시 방법이 갈린다** - `gameHud.ts`의 `BRIEF_COOLDOWN_MS` 참고.
   * 그래서 `remainingMs`만이 아니라 `totalMs`도 필요하다(게이지 비율 때문만이 아니다).
   */
  | Readonly<{ kind: 'cooldown'; remainingMs: number; totalMs: number }>
  /** 수색 페이즈가 아니라(또는 이 사람에게는) 애초에 쓸 수 없다. 버튼을 감춘다. */
  | Readonly<{ kind: 'unavailable' }>

/* --- 상수 ------------------------------------------------------------------- */

/**
 * 아직 원래 방에 간 적이 없는 상태. 라운드 시작(`gameState.freshRound`)과 수색 진입(`enterHunt`)이
 * 이 값을 편다. 리빌 진입은 이 값으로 되돌리지 **않는다** - 체류만 지우고 쿨타임은 남긴다.
 * 매치 리듀서도 같은 자리(라운드 시작·수색 진입)에서만 펴야 싱글과 패리티가 맞는다.
 */
export const NO_ORIGIN_VISIT: OriginVisit = Object.freeze({
  isInOriginRoom: false,
  originStayElapsedMs: 0,
  originCooldownMs: 0,
})

/* --- 전이 ------------------------------------------------------------------- */

/**
 * 원래 방에 들어간다. **탄환은 한 발도 주지 않는다** - 그 규칙은 이 파일이 탄환을 아예
 * 모른다는 것으로 표현된다(왜 보급이 빠졌는지는 `gameState.ts`의 `enterOriginRoom` 주석).
 *
 * 체류를 0에서 다시 센다. 직전 방문의 체류가 남아 있으면 두 번째 방문이 첫 방문의 시간을
 * 물려받아 즉시 쫓겨난다.
 */
export function enterOriginVisit(visit: OriginVisit): OriginVisit {
  return {
    isInOriginRoom: true,
    originStayElapsedMs: 0,
    originCooldownMs: visit.originCooldownMs,
  }
}

/**
 * 원래 방에서 나온다. 수동 퇴장과 체류 초과 자동 퇴장이 **같은 함수**를 쓴다 -
 * 갈라 두면 자동 퇴장에만 쿨타임을 거는 식의 구멍이 생기고, 그 구멍이 곧 깜빡비교다.
 *
 * 쿨타임은 남은 값과 무관하게 **가득** 채운다. 퇴장 시점에 쿨타임이 남아 있을 수는 없지만
 * (입장 조건이 쿨타임 0이다), 있더라도 더 짧아지는 쪽으로는 실패하지 않는다.
 */
export function exitOriginVisit(visit: OriginVisit, tuning: OriginVisitTuning): OriginVisit {
  // `visit`을 읽지 않는 것이 맞다 - 퇴장 결과는 들어가 있던 시간에 의존하지 않아야
  // 수동 퇴장과 자동 퇴장이 같은 상태를 만든다. 인자로 받는 이유는 세 전이의 모양을 맞춰
  // 매치 리듀서가 같은 꼴로 부를 수 있게 하기 위해서다.
  return {
    isInOriginRoom: false,
    originStayElapsedMs: 0,
    originCooldownMs: tuning.originCooldownSeconds * MS_PER_SECOND,
  }
}

/**
 * 시간을 흘린다. 안에 있으면 체류가, 밖에 있으면 쿨타임이 흐른다 - 둘이 동시에 흐르는 일은 없다.
 *
 * 변화가 없으면(밖 + 쿨타임 0) **입력 참조를 그대로** 돌려준다. 호출자가 "바뀐 게 있는가"를
 * 참조 비교로 알 수 있어야 불필요한 상태 복사와 효과 방출을 건너뛸 수 있다.
 *
 * 체류 상한을 넘기는 tick은 퇴장과 쿨타임 세팅을 **한 번에** 한다. "이번 tick은 상한에
 * 도달만 시키고 다음 tick에서 퇴장"으로 나누면 상한에 정확히 걸린 한 프레임 동안 원래 방에
 * 머무는 상태가 생기고, 그 프레임의 길이는 프레임레이트가 정한다 - 규칙이 시계에 의존하게 된다.
 */
export function tickOriginVisit(
  visit: OriginVisit,
  dtMs: number,
  tuning: OriginVisitTuning,
): OriginVisitTick {
  if (visit.isInOriginRoom) {
    const stayed = visit.originStayElapsedMs + dtMs
    if (stayed < tuning.originStaySeconds * MS_PER_SECOND) {
      return {
        visit: {
          isInOriginRoom: true,
          originStayElapsedMs: stayed,
          originCooldownMs: visit.originCooldownMs,
        },
        exited: false,
      }
    }
    return { visit: exitOriginVisit(visit, tuning), exited: true }
  }

  if (visit.originCooldownMs <= 0) return { visit, exited: false }
  return {
    visit: {
      isInOriginRoom: false,
      originStayElapsedMs: visit.originStayElapsedMs,
      originCooldownMs: Math.max(0, visit.originCooldownMs - dtMs),
    },
    exited: false,
  }
}

/* --- 셀렉터 ------------------------------------------------------------------ */

/** 원래 방에서 쫓겨나기까지 남은 시간(ms). 밖에 있으면 0. HUD 게이지가 읽는다. */
export function originStayRemainingMs(visit: OriginVisit, tuning: OriginVisitTuning): number {
  if (!visit.isInOriginRoom) return 0
  return Math.max(0, tuning.originStaySeconds * MS_PER_SECOND - visit.originStayElapsedMs)
}

/**
 * 방문 자체가 가능한가(밖에 있고 쿨타임이 0). 페이즈·역할·정원은 각 리듀서의 몫이다 -
 * 싱글은 `phase === 'hunt'`를, 매치는 문 토큰과 역할을 여기에 덧붙인다.
 */
export function canStartOriginVisit(visit: OriginVisit): boolean {
  return !visit.isInOriginRoom && visit.originCooldownMs <= 0
}

/* --- 얼굴 ------------------------------------------------------------------- */

/**
 * 방문 상태 → 버튼의 얼굴. 판단 근거는 전부 방문 상태에 있으므로 여기서 새로 계산하는 규칙은 없다 -
 * `totalMs`는 게이지의 분모로 튜닝 값을 ms로 옮긴 것뿐이다. **페이즈 게이트는 여기 없다**(`unavailable`을
 * 만드는 것은 호출자다): 싱글은 `phase !== 'hunt'`, 매치는 거기에 역할·관전을 더 보는데, 그 조건은 각 리듀서의
 * 것이고 이 리프는 사람 한 명의 방문만 안다.
 */
export function originStatusOf(visit: OriginVisit, tuning: OriginVisitTuning): OriginRoomStatus {
  if (visit.isInOriginRoom) {
    return {
      kind: 'staying',
      remainingMs: originStayRemainingMs(visit, tuning),
      totalMs: tuning.originStaySeconds * MS_PER_SECOND,
    }
  }
  if (visit.originCooldownMs > 0) {
    return {
      kind: 'cooldown',
      remainingMs: visit.originCooldownMs,
      totalMs: tuning.originCooldownSeconds * MS_PER_SECOND,
    }
  }
  return { kind: 'ready' }
}
